import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Plus, X, FileText, CheckCircle2, Download } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import FileUpload from '@/components/FileUpload';

function fmt(d) { return d ? new Date(d).toLocaleDateString() : '—'; }

/* eslint-disable -- v6 react-hooks/set-state-in-effect false-positive on the fetch + submit effects. */
export default function BoardMinutes() {
  const { id } = useParams();
  const [items, setItems] = useState([]);
  const [active, setActive] = useState(null);
  const [open, setOpen] = useState(false);
  const initial = {
    title: '', meeting_date: '', attendees: '', summary: '',
    items: [{ topic: '', discussion: '', decision: '', status: 'open' }],
  };
  const [form, setForm] = useState(initial);

  const load = async () => {
    try {
      const { data } = await api.get('/minutes/');
      setItems(data);
      if (id) {
        const r = await api.get(`/minutes/${id}`).catch(() => null);
        if (r) setActive(r.data);
      }
    } catch { toast.error('Could not load minutes'); }
  };
  useEffect(() => { load(); }, [id]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        title: form.title,
        meeting_date: new Date(form.meeting_date).toISOString(),
        attendees: form.attendees.split(',').map((s) => s.trim()).filter(Boolean),
        summary: form.summary,
        items: form.items.filter((i) => i.topic.trim()),
      };
      await api.post('/minutes/', payload);
      toast.success('Minutes recorded.');
      setOpen(false);
      setForm(initial);
      load();
    } catch { toast.error('Failed'); }
  };

  const addItem = () => setForm({ ...form, items: [...form.items, { topic: '', discussion: '', decision: '', status: 'open' }] });
  const updItem = (i, k, v) => {
    const items = [...form.items];
    items[i][k] = v;
    setForm({ ...form, items });
  };
  const rmItem = (i) => setForm({ ...form, items: form.items.filter((_, j) => j !== i) });

  const exportPdf = async (m) => {
    try {
      const base = process.env.REACT_APP_BACKEND_URL;
      const r = await fetch(`${base}/api/minutes/${m.minutes_id}/pdf`, {
        credentials: 'include',
      });
      if (!r.ok) throw new Error('Failed');
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `minutes_${(m.title || 'meeting').replace(/[^a-z0-9]/gi, '_')}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success('PDF downloaded.');
    } catch { toast.error('Could not export PDF.'); }
  };

  // detail view
  if (active) {
    return (
      <div>
        <div className="flex items-center justify-between">
          <Link to="/dashboard/minutes" className="ve-btn-ghost text-xs"><ArrowLeft size={12}/> All minutes</Link>
          <button onClick={() => exportPdf(active)} data-testid="minutes-export-pdf" className="ve-btn-secondary text-xs">
            <Download size={12}/> Export PDF
          </button>
        </div>
        <div className="mt-3">
          <div className="ve-eyebrow">Meeting minutes</div>
          <h1 className="display text-3xl md:text-4xl mt-2 font-bold">{active.title}</h1>
          <p className="text-sm text-slate-500 mt-1">{fmt(active.meeting_date)} · {(active.attendees || []).join(', ')}</p>
        </div>
        {active.summary && (
          <div className="mt-6 ve-card p-6">
            <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-bold">Summary</div>
            <p className="mt-2 text-sm whitespace-pre-wrap leading-relaxed">{active.summary}</p>
          </div>
        )}
        <div className="mt-6 space-y-3">
          {(active.items || []).map((it, i) => (
            <div key={i} className="ve-card p-5">
              <div className="flex items-center justify-between">
                <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">Item {i + 1}</div>
                <span className={`text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 rounded font-bold ${
                  it.status === 'done' ? 'bg-emerald-50 text-emerald-700' :
                  it.status === 'in_progress' ? 'bg-amber-50 text-amber-700' :
                  it.status === 'blocked' ? 'bg-rose-50 text-rose-700' :
                  'bg-slate-100 text-slate-600'
                }`}>{it.status?.replace('_', ' ')}</span>
              </div>
              <h3 className="display text-lg font-bold mt-2">{it.topic}</h3>
              <p className="text-sm text-slate-700 mt-2 whitespace-pre-wrap">{it.discussion}</p>
              {it.decision && (
                <div className="mt-3 p-3 rounded-md bg-[#FFF8E5] border border-[#8B3FC5]/40">
                  <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-[#5E1F8B] font-bold">
                    <CheckCircle2 size={12}/>Decision
                  </div>
                  <p className="text-sm mt-1">{it.decision}</p>
                </div>
              )}
            </div>
          ))}
        </div>
        <div className="mt-6">
          <FileUpload minutesId={active.minutes_id} kind="minutes" label="Attached documents"/>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="ve-eyebrow">Board & Management Minutes</div>
          <h1 className="display text-3xl md:text-4xl mt-2 font-bold">Decisions documented. Actions tracked.</h1>
          <p className="text-sm text-slate-600 mt-2 max-w-2xl">Every meeting captured — chair, attendees, agenda items, decisions, action owners, due dates.</p>
        </div>
        <button onClick={() => setOpen(true)} data-testid="minutes-create" className="ve-btn-primary self-start"><Plus size={14}/> New minutes</button>
      </div>

      <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {items.length === 0 && (
          <div className="ve-card p-10 text-center md:col-span-3">
            <FileText size={28} className="mx-auto text-slate-300 mb-3"/>
            <h3 className="display text-xl font-bold">No minutes recorded yet</h3>
          </div>
        )}
        {items.map((m) => (
          <Link key={m.minutes_id} to={`/dashboard/minutes/${m.minutes_id}`} className="ve-card p-6 group transition-all hover:-translate-y-1 hover:shadow-lg hover:border-[#8B3FC5]">
            <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-bold">{fmt(m.meeting_date)}</div>
            <h3 className="display text-xl mt-2 group-hover:text-[#5E1F8B] transition-colors font-bold">{m.title}</h3>
            <p className="text-sm text-slate-600 mt-2 line-clamp-2">{m.summary || `${m.items?.length || 0} agenda items`}</p>
            <div className="mt-4 flex items-center justify-between text-xs text-slate-500">
              <span>{m.attendees?.length || 0} attendees</span>
              <span className="mono">{m.items?.length || 0} items</span>
            </div>
          </Link>
        ))}
      </div>

      {/* Modal */}
      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={(e) => e.target === e.currentTarget && setOpen(false)}>
          <form onSubmit={submit} className="ve-card p-7 w-full max-w-3xl bg-white max-h-[88vh] overflow-y-auto">
            <div className="flex items-center justify-between sticky top-0 bg-white pb-3 border-b border-slate-200">
              <h2 className="display text-2xl font-bold">New meeting minutes</h2>
              <button type="button" onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-700"><X size={18}/></button>
            </div>
            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              <label className="md:col-span-2 text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">Title *
                <input required value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} className="mt-1.5 w-full rounded-md border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:border-[#8B3FC5] focus:ring-2 focus:ring-[#8B3FC5]/20 normal-case tracking-normal font-normal"/>
              </label>
              <label className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">Meeting date *
                <input type="date" required value={form.meeting_date} onChange={(e) => setForm({...form, meeting_date: e.target.value})} className="mt-1.5 w-full rounded-md border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:border-[#8B3FC5] focus:ring-2 focus:ring-[#8B3FC5]/20 font-normal"/>
              </label>
              <label className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">Attendees (comma-separated)
                <input value={form.attendees} onChange={(e) => setForm({...form, attendees: e.target.value})} placeholder="Chairman, CEO, CFO, …" className="mt-1.5 w-full rounded-md border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:border-[#8B3FC5] focus:ring-2 focus:ring-[#8B3FC5]/20 normal-case tracking-normal font-normal"/>
              </label>
              <label className="md:col-span-2 text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">Executive summary
                <textarea rows={3} value={form.summary} onChange={(e) => setForm({...form, summary: e.target.value})} className="mt-1.5 w-full rounded-md border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:border-[#8B3FC5] focus:ring-2 focus:ring-[#8B3FC5]/20 normal-case tracking-normal font-normal"/>
              </label>
            </div>
            <div className="mt-6">
              <div className="flex items-center justify-between">
                <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-bold">Agenda items</div>
                <button type="button" onClick={addItem} className="ve-btn-ghost text-xs"><Plus size={12}/> Add item</button>
              </div>
              <div className="mt-3 space-y-4">
                {form.items.map((it, i) => (
                  <div key={i} className="rounded-md border border-slate-200 p-4 bg-slate-50/40">
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">Item {i + 1}</span>
                      {form.items.length > 1 && <button type="button" onClick={() => rmItem(i)} className="text-slate-400 hover:text-rose-600"><X size={14}/></button>}
                    </div>
                    <input placeholder="Topic" value={it.topic} onChange={(e) => updItem(i, 'topic', e.target.value)} className="mt-2 w-full rounded-md border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:border-[#8B3FC5] focus:ring-2 focus:ring-[#8B3FC5]/20"/>
                    <textarea placeholder="Discussion" rows={2} value={it.discussion} onChange={(e) => updItem(i, 'discussion', e.target.value)} className="mt-2 w-full rounded-md border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:border-[#8B3FC5] focus:ring-2 focus:ring-[#8B3FC5]/20"/>
                    <textarea placeholder="Decision (optional)" rows={2} value={it.decision} onChange={(e) => updItem(i, 'decision', e.target.value)} className="mt-2 w-full rounded-md border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:border-[#8B3FC5] focus:ring-2 focus:ring-[#8B3FC5]/20"/>
                    <select value={it.status} onChange={(e) => updItem(i, 'status', e.target.value)} className="mt-2 rounded-md border border-slate-200 bg-white px-2 py-1.5 text-xs focus:outline-none focus:border-[#8B3FC5]">
                      <option value="open">Open</option>
                      <option value="in_progress">In progress</option>
                      <option value="done">Done</option>
                      <option value="blocked">Blocked</option>
                    </select>
                  </div>
                ))}
              </div>
            </div>
            <div className="mt-7 flex justify-end gap-3 pt-4 border-t border-slate-200">
              <button type="button" onClick={() => setOpen(false)} className="ve-btn-secondary">Cancel</button>
              <button type="submit" data-testid="minutes-submit" className="ve-btn-primary">Record minutes</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
